/* eslint-disable id-length */
import { GroupStep } from "~/lib/pipeline";
import BaseNode from "~/components/Playground/Node/BaseNode";
import { Node, NodeProps } from "@xyflow/react";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import Emojify from "~/components/shared/Emojify";
import { memo, MouseEvent, useCallback, useMemo } from "react";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import Icon from "~/components/shared/Icon";
import { GroupStepCount } from "~/components/build/Show/components/GroupStepCount";
import classNames from "classnames";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { GroupStepHoverCard } from "~/components/Playground/Node/GroupStepNode/GroupStepHoverCard";

type GroupStepNodeType = Node<GroupStep, "group">;

function GroupStepNode(props: NodeProps<GroupStepNodeType>) {
  const { build } = useBuild({ strict: false });
  const isGroupStepExpanded = useBuildPageStore((state) => state.isGroupStepExpanded);
  const expandGroupSteps = useBuildPageStore((state) => state.expandGroupSteps);
  const collapseGroupSteps = useBuildPageStore((state) => state.collapseGroupSteps);
  const { missingDependencies } = useStepDependencies(props.id);
  const stepsGroupedByParent = useBuildPageStore((state) => state.stepsGroupedByParent);

  const isCollapsed = !isGroupStepExpanded(props.id);

  const annotationCountsByStyle = useMemo(() => {
    const children = stepsGroupedByParent.get(props.id) || [];

    return children.reduce<Record<string, number>>((aggregated, child) => {
      const counts = child.statistics?.annotationCountsByStyle;
      if (counts) {
        for (const [style, count] of Object.entries(counts)) {
          aggregated[style] = (aggregated[style] || 0) + count;
        }
      }
      return aggregated;
    }, {});
  }, [stepsGroupedByParent, props.id]);

  /**
   * Expand group step when clicking on the node.
   *
   * Don't collapse the group step when clicking on it, as this would be
   * counter-intuitive for users. Instead, we only expand the group step
   * when it is not already expanded.
   */
  const handleNodeClick = useCallback(() => {
    if (!isGroupStepExpanded(props.id)) {
      expandGroupSteps([props.id]);
    }
  }, [isGroupStepExpanded, expandGroupSteps, props.id]);

  const toggleGroup = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();

      isGroupStepExpanded(props.id) ? collapseGroupSteps([props.id]) : expandGroupSteps([props.id]);
    },
    [isGroupStepExpanded, expandGroupSteps, collapseGroupSteps, props.id],
  );

  const baseNode = (
    <BaseNode
      // For groups steps the node width and height is determined by ELK layout on parent element.
      // Therefore, we need to set width and height to 100% to allow the node to fill the available space.
      className={classNames("group h-full w-full max-w-none hover:no-underline", {
        "bg-white/40": !isCollapsed,
      })}
      {...props}
      label={<Emojify text={props.data.label} title="" />}
      count={build ? <GroupStepCount step={props.data} /> : undefined}
      annotationCountsByStyle={isCollapsed ? annotationCountsByStyle : undefined}
      prefix={
        <button
          onClick={toggleGroup}
          className="text-charcoal-700 flex items-center rounded-sm border-transparent bg-gray-100 p-0.5 text-xs transition-transform duration-75 hover:border-purple-600 hover:bg-purple-100 hover:text-purple-600 active:translate-y-0.5 active:bg-purple-100 active:text-purple-600"
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          <Icon
            icon={
              isCollapsed
                ? "custom/outline/16x/chevrons-up-down"
                : "custom/outline/16x/chevrons-down-up"
            }
            className="block h-4 w-4"
          />
        </button>
      }
    />
  );

  const nodeWrapper = (
    <div className="h-full w-full max-w-none bg-white" onClick={handleNodeClick}>
      {baseNode}
    </div>
  );

  return (
    <HoverCard.Root>
      <HoverCard.Portal>
        {isCollapsed && build && props.data.state && (
          <GroupStepHoverCard
            step={props.data}
            build={build}
            missingDependencies={missingDependencies}
          />
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>{nodeWrapper}</HoverCard.Trigger>
    </HoverCard.Root>
  );
}

export default memo(GroupStepNode);
